import { api, tokenStore } from './client'
import type { AuthResult, CurrentUser } from './types'

/** What `POST /auth/intuit/exchange` returns: a session plus the company it connected. */
export interface IntuitExchangeResult {
  accessToken: string
  refreshToken: string
  expiresAt: string
  companyId: string
}

export const authApi = {
  login(email: string, password: string): Promise<AuthResult> {
    return api.post<AuthResult>('/auth/login', { email, password }, { anonymous: true })
  },

  register(email: string, password: string, companyName: string): Promise<AuthResult> {
    return api.post<AuthResult>('/auth/register', { email, password, companyName }, { anonymous: true })
  },

  me(): Promise<CurrentUser> {
    return api.get<CurrentUser>('/auth/me')
  },

  async logout(): Promise<void> {
    const refreshToken = tokenStore.read()?.refreshToken
    if (!refreshToken) return
    // A failed logout must not strand the user in the app, so swallow it —
    // the caller clears local tokens either way.
    try {
      await api.post<void>('/auth/logout', { refreshToken })
    } catch {
      /* server-side revoke is best-effort */
    }
  },

  /** Returns the Intuit OAuth URL for QuickBooks sign-in. */
  intuitLoginUrl(): Promise<{ url: string }> {
    return api.get<{ url: string }>('/auth/intuit-login-url', { anonymous: true })
  },

  /**
   * Redeems the one-time code from the QuickBooks sign-in redirect.
   *
   * The callback cannot put the tokens in the redirect URL — that would write a
   * month-long refresh token into browser history and every proxy log on the way
   * — so it hands over a short-lived code and the tokens come back in this
   * response body instead.
   */
  exchangeIntuitCode(code: string): Promise<IntuitExchangeResult> {
    return api.post<IntuitExchangeResult>(
      '/auth/intuit/exchange',
      { code },
      { anonymous: true },
    )
  },
}
